import { Injectable } from '@angular/core';
import { NativeDateAdapter } from '@angular/material/core';

const DATE_INPUT_FORMAT = {
  year: 'numeric' as const,
  month: 'numeric' as const,
  day: 'numeric' as const,
};

// Reference date: Feb 22, 2033 — day(22) > 12 so it can't be confused with month(2).
const REFERENCE_DATE = new Date(2033, 1, 22);

/**
 * Fixes NativeDateAdapter.parse() to be locale-aware by reusing
 * NativeDateAdapter.format() to derive the locale's date part order.
 */
@Injectable()
export class LocaleDateAdapter extends NativeDateAdapter {
  private datePartOrder: ('day' | 'month' | 'year')[] | null = null;

  /**
   * Use this.format() (inherited from NativeDateAdapter) on a known
   * reference date to determine the locale's day/month/year order.
   */
  private getDatePartOrder(): ('day' | 'month' | 'year')[] {
    if (!this.datePartOrder) {
      const formatted = this.format(REFERENCE_DATE, DATE_INPUT_FORMAT);
      const parts = formatted.match(/\d+/g) ?? [];
      this.datePartOrder = parts.map((p) => {
        if (p === '22') return 'day';
        if (p === '2') return 'month';
        return 'year';
      });
    }
    return this.datePartOrder;
  }

  override parse(value: string | number): Date | null {
    if (typeof value === 'number') return new Date(value);
    if (!value) return null;

    const match = value.trim().match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (!match) return super.parse(value);

    const order = this.getDatePartOrder();
    const values: Record<string, number> = {};
    order.forEach((type, i) => (values[type] = +match[i + 1]));

    const day = values['day'];
    const month = values['month'];
    const year = values['year'];

    if (month < 1 || month > 12 || day < 1 || day > 31) return null;

    const date = new Date(year, month - 1, day);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }

    return date;
  }
}
